import { useQuery, useQueryClient } from '@tanstack/react-query';
import { App as AntdApp, Button, Result, Spin } from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { api, ApiError } from '../shared/api-client.js';
import { SessionContext } from './session-context.js';

export function SessionBoundary({ children }: { readonly children: ReactNode }) {
  const { message: messageApi } = AntdApp.useApp();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const expired = useRef(false);
  const [accessRevoked, setAccessRevoked] = useState(false);
  const [authExpired, setAuthExpired] = useState(false);
  const sessionQuery = useQuery({
    enabled: !accessRevoked && !authExpired,
    queryFn: api.me,
    queryKey: ['session'],
    retry: false,
  });

  const currentContext = sessionQuery.data
    ? JSON.stringify([
        sessionQuery.data.userId,
        sessionQuery.data.employeeId,
        sessionQuery.data.role,
        [...sessionQuery.data.capabilities].sort(),
      ])
    : undefined;
  const [acceptedContext, setAcceptedContext] = useState(currentContext);
  if (acceptedContext === undefined && currentContext !== undefined) {
    setAcceptedContext(currentContext);
  }
  const contextChanged =
    acceptedContext !== undefined &&
    currentContext !== undefined &&
    acceptedContext !== currentContext;
  useEffect(() => {
    if (!contextChanged) return;
    let active = true;
    // The changed-context render has already unmounted private children and drafts.
    const privateQueries = {
      predicate: (query: { queryKey: readonly unknown[] }) => query.queryKey[0] !== 'session',
    };
    void queryClient.cancelQueries(privateQueries).then(() => {
      if (!active) return;
      queryClient.removeQueries(privateQueries);
      queryClient.getMutationCache().clear();
      void navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: null },
      );
      setAcceptedContext(currentContext);
    });
    return () => {
      active = false;
    };
  }, [
    contextChanged,
    currentContext,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    queryClient,
  ]);

  useEffect(() => {
    const onExpired = (event: Event) => {
      if (expired.current) return;
      expired.current = true;
      setAuthExpired(true);
      const detail =
        event instanceof CustomEvent && typeof event.detail === 'string'
          ? event.detail
          : '登录状态已失效';
      void queryClient.cancelQueries();
      queryClient.clear();
      void messageApi.warning(detail);
    };
    const onRevoked = () => {
      setAccessRevoked(true);
      void queryClient.cancelQueries();
      queryClient.clear();
    };
    window.addEventListener('private-access-revoked', onRevoked);
    window.addEventListener('auth-expired', onExpired);
    return () => {
      window.removeEventListener('private-access-revoked', onRevoked);
      window.removeEventListener('auth-expired', onExpired);
    };
  }, [messageApi, queryClient]);

  if (authExpired) return <Navigate to="/login" replace />;
  // Unmount private content and dirty forms before any navigation can be blocked.
  if (accessRevoked)
    return (
      <Result
        status="403"
        title="访问权限已更新"
        subTitle="已清除当前页面的私密内容和未保存草稿。"
        extra={
          <Button
            onClick={() => {
              void navigate('/', { replace: true, flushSync: true });
              setAccessRevoked(false);
            }}
          >
            返回首页
          </Button>
        }
      />
    );
  if (sessionQuery.isPending || contextChanged) {
    return (
      <div className="app-loading" role="status" aria-label="正在验证登录状态">
        <Spin size="large" />
      </div>
    );
  }
  if (sessionQuery.data === undefined) {
    const state =
      sessionQuery.error instanceof ApiError && sessionQuery.error.status !== 401
        ? { error: sessionQuery.error.message }
        : { from: location.pathname };
    return <Navigate to="/login" replace state={state} />;
  }
  return (
    <SessionContext.Provider key={currentContext} value={{ session: sessionQuery.data }}>
      {children}
    </SessionContext.Provider>
  );
}
